import { useCallback } from 'react';
import { BUFFER_SIZE } from './types';
import type { MapEntityPath } from './types';

interface RiverSnapInput {
    landMaskRef: React.RefObject<Uint8Array | null>;
    mapEntityPaths: MapEntityPath[];
}

const SNAP_THRESHOLD = 18;
const COAST_SEARCH_RADIUS = 16;

/**
 * Returns stable snap helpers for rivers:
 * - snapToWaterCoast — snaps a map coordinate to the nearest water/coast pixel
 * - snapToOtherRivers — snaps to the nearest vertex or segment on another river path
 * - snapRiverNode — tries river snap first, then falls back to coast snap
 */
export function useRiverSnap({ landMaskRef, mapEntityPaths }: RiverSnapInput) {
    const snapToWaterCoast = useCallback(
        (x: number, y: number): { x: number; y: number } => {
            const mask = landMaskRef.current;
            if (!mask) return { x, y };
            const ix = Math.max(0, Math.min(BUFFER_SIZE - 1, Math.round(x)));
            const iy = Math.max(0, Math.min(BUFFER_SIZE - 1, Math.round(y)));
            const val = mask[iy * BUFFER_SIZE + ix];
            if (val < 128) return { x, y };

            let closestX = x;
            let closestY = y;
            let minDist = Infinity;

            for (let dy = -COAST_SEARCH_RADIUS; dy <= COAST_SEARCH_RADIUS; dy++) {
                for (let dx = -COAST_SEARCH_RADIUS; dx <= COAST_SEARCH_RADIUS; dx++) {
                    const nx = ix + dx;
                    const ny = iy + dy;
                    if (nx >= 0 && nx < BUFFER_SIZE && ny >= 0 && ny < BUFFER_SIZE) {
                        const nVal = mask[ny * BUFFER_SIZE + nx];
                        if (nVal < 128) {
                            const dist = Math.hypot(dx, dy);
                            if (dist < minDist) {
                                minDist = dist;
                                closestX = nx;
                                closestY = ny;
                            }
                        }
                    }
                }
            }
            return minDist <= COAST_SEARCH_RADIUS ? { x: closestX, y: closestY } : { x, y };
        },
        [landMaskRef],
    );

    const snapToOtherRivers = useCallback(
        (x: number, y: number, excludePathUid?: string): { x: number; y: number } => {
            let closestX = x;
            let closestY = y;
            let minDist = Infinity;

            for (const path of mapEntityPaths) {
                if (path.pathType !== 'river') continue;
                if (excludePathUid && path.uid === excludePathUid) continue;

                for (const v of path.vertices) {
                    const d = Math.hypot(x - v.x, y - v.y);
                    if (d < minDist && d < SNAP_THRESHOLD) {
                        minDist = d;
                        closestX = v.x;
                        closestY = v.y;
                    }
                }

                for (let i = 0; i < path.vertices.length - 1; i++) {
                    const a = path.vertices[i];
                    const b = path.vertices[i + 1];
                    const dx = b.x - a.x;
                    const dy = b.y - a.y;
                    const lenSq = dx * dx + dy * dy;
                    if (lenSq === 0) continue;
                    const t = Math.max(0, Math.min(1, ((x - a.x) * dx + (y - a.y) * dy) / lenSq));
                    const projX = a.x + t * dx;
                    const projY = a.y + t * dy;
                    const d = Math.hypot(x - projX, y - projY);
                    if (d < minDist && d < SNAP_THRESHOLD) {
                        minDist = d;
                        closestX = projX;
                        closestY = projY;
                    }
                }
            }
            return { x: closestX, y: closestY };
        },
        [mapEntityPaths],
    );

    const snapRiverNode = useCallback(
        (x: number, y: number, excludePathUid?: string): { x: number; y: number } => {
            const riverSnap = snapToOtherRivers(x, y, excludePathUid);
            if (riverSnap.x !== x || riverSnap.y !== y) return riverSnap;
            return snapToWaterCoast(x, y);
        },
        [snapToOtherRivers, snapToWaterCoast],
    );

    return { snapToWaterCoast, snapToOtherRivers, snapRiverNode };
}
